// src/pages/Community/StoryCharactersIndex.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../firebase";
import "./css/QuickPanel.css";
import "./css/StoryCharactersIndex.css";
import ToolbarHeader, { GTA_GAME_TABS } from "./components/ToolbarHeader";
import Modal from "./components/Modal";
import CharacterForm from "./StoryCharacters/CharacterStoryForm";
import EmptyState from "./components/EmptyState";
import DetailSidePanel from "./components/DetailSidePanel";
import CharacterDetailPanel from "./StoryCharacters/CharacterDetailPanel";

export default function StoryCharactersIndex({ user }) {
  const [characters, setCharacters] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedKey = searchParams.get("game") || "gta-6";
  const setSelectedKey = (key) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("game", key);
      return p;
    });
  };
  const isProtagonist = (c) => {
    const v = (
      c.characterType ||
      ""
    )
      .toString()
      .toLowerCase();

    return (
      v === "protagonist"
    );
  };


  const [showModal, setShowModal] = useState(false);
  const isSuperAdmin = user?.role === "superadmin";
  const isAdmin = isSuperAdmin || user?.role === "admin";
  const canAddCharacter = isAdmin;
  const [activeCharacterId, setActiveCharacterId] = useState(null);
  const navigate = useNavigate();

  const norm = (s) => (s || "").toString().trim().toLowerCase();
  const isMine = (c, user) => {
    const uid = user?.uid;
    return !!uid && [c.authorUid, c.createdBy, c.userId, c.ownerId].some(
      (x) => x === uid
    );
  };

  useEffect(() => {
    const q = query(collection(db, "characters"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort(
            (a, b) =>
              (b.createdAt?.toMillis?.() || 0) -
              (a.createdAt?.toMillis?.() || 0)
          );
        setCharacters(docs);
        setLoading(false);
      },
      (err) => {
        console.error("characters query error:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const list = useMemo(() => {
    let arr = [...characters];

    if (selectedKey === "mine" && isAdmin) {
      arr = arr.filter((c) => isMine(c, user));
    } else if (selectedKey === "mine") {
      arr = []; // or fall back to "all"
    }
    else if (selectedKey !== "all") {
      const f = norm(selectedKey);
      arr = arr.filter((c) => norm(c.gameKey || c.game) === f);
    }

    if (search.trim()) {
      const q = norm(search);
      arr = arr.filter((c) =>
        [
          c.characterName,
          c.authorName,
          c.game,
          c.gameKey,
          c.synopsis,
          c.story,
          ...(c.tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    arr.sort((a, b) => {
      // 1️⃣ Protagonists first
      const pa = isProtagonist(a) ? 0 : 1;
      const pb = isProtagonist(b) ? 0 : 1;
      if (pa !== pb) return pa - pb;

      // 2️⃣ Alphabetical by name
      const an = norm(a.characterName);
      const bn = norm(b.characterName);
      if (an && bn && an !== bn) return an.localeCompare(bn);

      // 3️⃣ Fallback: newest first
      return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
    });


    return arr;
  }, [characters, search, selectedKey, user]);

  const labelForGameKey = (gk) => {
    if (!gk) return "Unknown";
    const up = gk.toUpperCase().replace("-", " ");
    const key = gk.toLowerCase();
    if (key === "gta-6") return "GTA VI";
    if (key === "gta-5") return "GTA V";
    if (key === "gta-4" || key === "gta-iv") return "GTA IV";
    return up;
  };

  return (
    <>
      <div id="sc" className="char-wrap">
        <ToolbarHeader
          children={
            <div className="forum-tabs">
              {GTA_GAME_TABS
                .concat(isAdmin ? [{ key: "mine", label: "My Characters" }] : [])
                .map(({ key, label }) => {
                  const active = selectedKey === key;
                  return (
                    <span
                      key={key}
                      className={`tab-text ${active ? "active" : ""}`}
                      onClick={() => setSelectedKey(key)}
                      role="button"
                      aria-current={active ? "page" : undefined}
                    >
                      {label}
                    </span>
                  );
                })}

            </div>
          }
          search={search}
          setSearch={setSearch}
          placeholder="Search characters, games, tags…"
          user={user}
          onAddMission={canAddCharacter ? () => setShowModal(true) : undefined}
          addLabel={canAddCharacter ? "＋ Add Character" : null}
        />
      </div>
      <div className={`msn-shell ${activeCharacterId ? "panel-open" : ""}`}>
        <div className="msn-wrap">
          <div id="sc" className="char-wrap">
            <ul className="char-layout">
              {(loading) &&
                Array.from({ length: 6 }).map((_, i) => (
                  <li key={`sk-${i}`} className="char-card char-card--skeleton">
                    <div className="char-thumb skeleton" />
                    <div className="char-card-body">
                      <div className="skeleton skeleton-line" />
                      <div className="skeleton skeleton-line short" />
                    </div>
                  </li>
                ))}

              {!loading &&
                list.map((c) => (
                  <li key={c.id} className="char-card" onClick={() => setActiveCharacterId(c.id)}>
                    {/* Thumbnail + overlay now contains the author/role/snippet */}
                    <div className="char-thumb">
                      {c.imageUrl ? (
                        <img
                          className="char-img"
                          src={c.imageUrl}
                          alt={c.characterName || "Character"}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="char-img char-img--placeholder">🃏</div>
                      )}

                      <div className="char-toprow">
                        <span className="char-badge">
                          {(GTA_GAME_TABS.find((g) => g.key === norm(c.gameKey || c.game))?.label) ||
                            labelForGameKey(norm(c.gameKey || c.game))}
                        </span>

                        {isMine(c, user) && (
                          <span
                            className="char-chip char-chip--owner"
                            title="You created this"
                          >
                            👑
                          </span>
                        )}
                      </div>

                      <div className="char-titlebar">

                      </div>

                      {/* NEW: overlay with author / role / snippet on top of image */}
                      <div className="char-overlay" aria-hidden={false}>
                        <div className="char-overlay-line char-overlay-author">
                          <h6 className="overlay-name">{c.characterName || "Unnamed"}</h6>
                          {c.authorName && (
                            <span className="overlay-author">by {c.authorName}</span>
                          )}
                        </div>

                        {(c.synopsis || c.story) && (
                          <div className="char-overlay-line char-overlay-synopsis">
                            {((c.synopsis && c.synopsis.trim()) || (c.story && c.story.trim()) || "")
                              .slice(0, 140)
                              .replace(/\s+\S*$/, (m) => m) /* avoid mid-word cut as best-effort */}
                            {((c.synopsis || c.story) || "").length > 140 && "…"}
                          </div>
                        )}

                      </div>
                    </div>
                  </li>
                ))}

              {!loading && list.length === 0 && (
                <li className="char-empty empty-block">
                  <EmptyState
                    icon="🃏"
                    title="No Characters Available"
                    description="Please stay tuned. New characters will be added soon."
                  />
                </li>
              )}
            </ul>
          </div>
        </div>
        {/* RIGHT: SIDE PANEL */}
        <DetailSidePanel
          open={!!activeCharacterId}
          title="Character Details"
          onClose={() => setActiveCharacterId(null)}
          onMaximize={() =>
            navigate(`/story-characters/${activeCharacterId}?game=${selectedKey}`)
          }
        >
          <CharacterDetailPanel
            characterId={activeCharacterId}
            user={user}
          />
        </DetailSidePanel>
      </div >

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Add a Character"
        size="md"
        closeOnBackdrop={true}
        showClose={true}
      >
        {user ? (
          <CharacterForm
            user={user}
            onDone={() => setShowModal(false)}
            onCancel={() => setShowModal(false)}
          />
        ) : (
          <div style={{ padding: 16 }}>Please log in to continue.</div>
        )}
      </Modal>

    </>
  );
}
